import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Trash2 } from 'lucide-react'
import { Card } from '../../components/Card'
import { Badge } from '../../components/Badge'
import { Button } from '../../components/Button'
import { CopyButton } from '../../components/CopyButton'
import { Modal } from '../../components/Modal'
import { ToastProvider, useToast } from '../../components/Toast'
import { useDb } from '../../state/DbProvider'
import {
  agencies,
  agenciesForSuperAgency,
  connectorsForAgency,
  findUser,
  getCurrentUser,
  isActiveUser,
  superAgencies,
} from '../../state/selectors'
import type { BankAccount, User } from '../../state/types'
import { buildAgencyInviteLink, buildSuperAgencyAgencyInviteLink } from '../../utils/affiliate'
import { formatPercent } from '../../utils/format'

const actionButtonClass =
  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50'

function formatRateInput(rate?: number) {
  if (rate === undefined) return ''
  return String(Math.round(rate * 10000) / 100)
}

function parseRateInput(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return undefined
  return parsed / 100
}

function getDefaultRate(user: User, agencyRate: number, connectorRate: number) {
  return user.role === '代理店' ? agencyRate : connectorRate
}

function getCurrentOverride(user: User) {
  return user.role === '代理店' ? user.agencyRateOverride : user.connectorRateOverride
}

function getEffectiveRate(user: User, agencyRate: number, connectorRate: number) {
  const override = getCurrentOverride(user)
  return override ?? getDefaultRate(user, agencyRate, connectorRate)
}

function roleOrder(role: User['role']) {
  if (role === '総代理店') return 0
  if (role === '代理店') return 1
  if (role === 'コネクター') return 2
  return 3
}

function AdminPartnersInner() {
  const { db, actions } = useDb()
  const toast = useToast()
  const [activeCommissionAction, setActiveCommissionAction] = useState<string | null>(null)
  const [activeOrgAction, setActiveOrgAction] = useState<string | null>(null)
  const [commissionDrafts, setCommissionDrafts] = useState<Record<string, string>>({})
  const [agencyParentDrafts, setAgencyParentDrafts] = useState<Record<string, string>>({})
  const [expandedAgencyIds, setExpandedAgencyIds] = useState<Set<string>>(() => new Set())
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
  const [activeDeleteAction, setActiveDeleteAction] = useState<string | null>(null)
  if (!db) return null
  const me = getCurrentUser(db)

  if (me.role !== 'J-Navi管理者') {
    return (
      <Card title="組織/ユーザー（運営）" subtitle="このページはJnavi運営向けです">
        <div className="text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const superAgencyList = useMemo(() => superAgencies(db), [db.users])
  const agencyList = useMemo(() => agencies(db), [db.users])
  const assignableParents = useMemo(() => [...superAgencyList, ...agencyList], [superAgencyList, agencyList])
  const payoutUsers = useMemo(
    () =>
      db.users
        .filter(
          (user) =>
            isActiveUser(user)
            && (user.role === '総代理店' || user.role === '代理店' || user.role === 'コネクター'),
        )
        .slice()
        .sort((a, b) => {
          const orderDelta = roleOrder(a.role) - roleOrder(b.role)
          if (orderDelta !== 0) return orderDelta
          return a.name.localeCompare(b.name, 'ja')
        }),
    [db.users],
  )
  const commissionUsers = useMemo(
    () => payoutUsers.filter((user) => user.role === '代理店' || user.role === 'コネクター'),
    [payoutUsers],
  )
  const pendingAgencySignupRequestCount = useMemo(
    () => db.agencySignupRequests.filter((request) => request.status === '審査待ち').length,
    [db.agencySignupRequests],
  )

  const toggleAgencyExpanded = (agencyId: string) => {
    setExpandedAgencyIds((current) => {
      const next = new Set(current)
      if (next.has(agencyId)) next.delete(agencyId)
      else next.add(agencyId)
      return next
    })
  }

  useEffect(() => {
    const nextDrafts = Object.fromEntries(
      commissionUsers.map((user) => [user.id, formatRateInput(getCurrentOverride(user))]),
    )
    setCommissionDrafts(nextDrafts)
  }, [commissionUsers])

  useEffect(() => {
    const nextDrafts = Object.fromEntries(
      agencyList.map((agency) => [agency.id, agency.agency?.superAgencyId ?? '']),
    )
    setAgencyParentDrafts(nextDrafts)
  }, [agencyList])

  const handleSaveCommission = async (user: User) => {
    const parsedRate = parseRateInput(commissionDrafts[user.id] ?? '')
    if (parsedRate === undefined) {
      toast.show('報酬率は数値で入力してください', 'error')
      return
    }
    if (parsedRate !== null && !(parsedRate >= 0 && parsedRate <= 1)) {
      toast.show('報酬率は 0 から 100 の範囲で入力してください', 'error')
      return
    }

    if (user.role === '代理店' && parsedRate !== null && parsedRate > db.settings.superAgencyRate) {
      toast.show(`代理店総率は総代理店総率 ${formatPercent(db.settings.superAgencyRate)} 以下にしてください`, 'error')
      return
    }

    if (user.role === 'コネクター' && parsedRate !== null) {
      const parentAgency = findUser(db, user.connector?.agencyId)
      const maxRate =
        parentAgency?.role === '総代理店'
          ? db.settings.superAgencyRate
          : parentAgency
            ? getEffectiveRate(parentAgency, db.settings.agencyRate, db.settings.connectorRate)
            : db.settings.agencyRate

      if (parsedRate > maxRate) {
        toast.show(`コネクター報酬率は所属先の総率 ${formatPercent(maxRate)} 以下にしてください`, 'error')
        return
      }
    }

    setActiveCommissionAction(`${user.id}:save`)
    const result = await actions.adminSetUserCommissionOverride({
      userId: user.id,
      agencyRateOverride: user.role === '代理店' ? parsedRate : undefined,
      connectorRateOverride: user.role === 'コネクター' ? parsedRate : undefined,
    })
    setActiveCommissionAction(null)

    if (!result.ok) {
      toast.show(result.message ?? '個別報酬率を保存できませんでした', 'error')
      return
    }

    toast.show(parsedRate === null ? '個別報酬率を既定値に戻しました' : '個別報酬率を更新しました', 'success')
  }

  const handleResetCommission = async (user: User) => {
    setActiveCommissionAction(`${user.id}:reset`)
    const result = await actions.adminSetUserCommissionOverride({
      userId: user.id,
      agencyRateOverride: user.role === '代理店' ? null : undefined,
      connectorRateOverride: user.role === 'コネクター' ? null : undefined,
    })
    setActiveCommissionAction(null)

    if (!result.ok) {
      toast.show(result.message ?? '個別報酬率を既定値に戻せませんでした', 'error')
      return
    }

    setCommissionDrafts((current) => ({ ...current, [user.id]: '' }))
    toast.show('個別報酬率を既定値に戻しました', 'success')
  }

  const handleDeletePartnerAccount = async () => {
    if (!deleteTarget) return

    setActiveDeleteAction(deleteTarget.id)
    const result = await actions.adminDeletePartnerAccount(deleteTarget.id)
    setActiveDeleteAction(null)

    if (!result.ok) {
      toast.show(result.message ?? 'アカウントを削除できませんでした', 'error')
      return
    }

    toast.show(`${deleteTarget.name} のアカウントを削除しました`, 'success')
    setDeleteTarget(null)
  }

  return (
    <div className="space-y-6">
      <Card
        title="代理店申請承認"
        subtitle="代理店申請の確認と承認は専用ページに移動しました。"
        right={
          <Badge tone={pendingAgencySignupRequestCount > 0 ? 'amber' : 'slate'}>
            審査待ち {pendingAgencySignupRequestCount} 件
          </Badge>
        }
      >
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-600">
            申請内容の確認、承認、却下、アカウント発行案内メール送信は専用ページから行ってください。
          </div>
          <Link
            to="/admin/agency-approvals"
            className="inline-flex items-center justify-center rounded-lg bg-[var(--jnavi-navy)] px-4 py-2 text-sm font-medium text-white transition hover:brightness-110"
          >
            承認ページを開く
          </Link>
        </div>
      </Card>

      <Card title="総代理店一覧" subtitle="総代理店の招待コード、直属コネクター、配下代理店を確認できます。">
        <div className="grid gap-4 px-5 py-4">
          {superAgencyList.map((superAgency) => {
            const inviteCode = superAgency.agency?.inviteCode ?? ''
            const connectorInviteLink = inviteCode ? buildAgencyInviteLink(inviteCode) : ''
            const agencyInviteLink = inviteCode ? buildSuperAgencyAgencyInviteLink(inviteCode) : ''
            const directConnectors = connectorsForAgency(db, superAgency.id)
            const childAgencies = agenciesForSuperAgency(db, superAgency.id)

            return (
              <div key={superAgency.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-slate-900">{superAgency.name}</div>
                      <Badge tone="blue">総代理店</Badge>
                    </div>
                    <div className="text-xs text-slate-500">{superAgency.email}</div>
                  </div>
                  <div className="text-right text-sm text-slate-700">
                    <div>直属コネクター: {directConnectors.length}人</div>
                    <div>配下代理店: {childAgencies.length}社</div>
                  </div>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <InviteCell title="コネクター招待" inviteCode={inviteCode} link={connectorInviteLink} />
                  <InviteCell title="代理店申請招待" inviteCode={inviteCode} link={agencyInviteLink} />
                </div>
              </div>
            )
          })}

          {superAgencyList.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              総代理店はまだいません。
            </div>
          ) : null}
        </div>
      </Card>

      <Card
        title="代理店・所属コネクター管理"
        subtitle="代理店ごとに所属コネクターをまとめて表示します。所属変更や削除も同じまとまりの中で操作できます。"
      >
        <div className="grid gap-4 px-5 py-4">
          {agencyList.map((agency) => {
            const isBusy = activeOrgAction?.startsWith(`${agency.id}:`) ?? false
            const childConnectors = connectorsForAgency(db, agency.id)
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
            const childCount = childConnectors.length
            const parentSuperAgency = findUser(db, agency.agency?.superAgencyId)
            const isExpanded = expandedAgencyIds.has(agency.id)
            const panelId = `agency-panel-${agency.id}`

            return (
              <div key={agency.id} className="rounded-xl border border-slate-200 bg-white">
                <div
                  className={`flex flex-col gap-3 p-4 lg:flex-row lg:items-start lg:justify-between ${
                    isExpanded ? 'border-b border-slate-100' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-3 rounded-lg p-2 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    onClick={() => toggleAgencyExpanded(agency.id)}
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900">{agency.name}</span>
                        <Badge tone="blue">代理店</Badge>
                        <Badge tone={childCount > 0 ? 'green' : 'slate'}>所属コネクター {childCount}人</Badge>
                      </span>
                      <span className="mt-1 block text-xs text-slate-500">{agency.email}</span>
                      <span className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                        <span className="rounded-md bg-slate-50 px-2 py-1">
                          招待コード: <span className="font-mono">{agency.agency?.inviteCode ?? '-'}</span>
                        </span>
                        <span className="rounded-md bg-slate-50 px-2 py-1">
                          親総代理店: {parentSuperAgency?.name ?? '未設定'}
                        </span>
                      </span>
                    </span>
                  </button>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
                      disabled={isBusy}
                      onClick={async () => {
                        if (!window.confirm(`${agency.name} を総代理店へ格上げしますか？`)) return
                        setActiveOrgAction(`${agency.id}:promote`)
                        const result = await actions.adminPromoteAgencyToSuperAgency(agency.id)
                        setActiveOrgAction(null)
                        if (!result.ok) toast.show(result.message ?? '総代理店へ格上げできませんでした', 'error')
                        else toast.show('総代理店へ格上げしました', 'success')
                      }}
                    >
                      総代理店へ格上げ
                    </button>
                    <button
                      type="button"
                      className={`${actionButtonClass} border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100`}
                      disabled={isBusy || childCount > 0}
                      title={childCount > 0 ? '配下コネクターを付け替えてから削除してください' : 'アカウントを削除'}
                      onClick={() => setDeleteTarget(agency)}
                    >
                      <Trash2 size={14} />
                      削除
                    </button>
                  </div>
                </div>

                {isExpanded ? (
                  <div id={panelId} className="grid gap-4 p-4 lg:grid-cols-[minmax(220px,0.75fr)_minmax(0,1.5fr)]">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold text-slate-700">親総代理店</div>
                      <select
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm"
                        value={agencyParentDrafts[agency.id] ?? ''}
                        onChange={(event) =>
                          setAgencyParentDrafts((current) => ({
                            ...current,
                            [agency.id]: event.target.value,
                          }))
                        }
                      >
                        <option value="">未設定</option>
                        {superAgencyList.map((superAgency) => (
                          <option key={superAgency.id} value={superAgency.id}>
                            {superAgency.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className={`${actionButtonClass} mt-3 w-full justify-center border-slate-200 bg-white text-slate-700 hover:bg-slate-100`}
                        disabled={isBusy}
                        onClick={async () => {
                          setActiveOrgAction(`${agency.id}:parent`)
                          const result = await actions.adminSetAgencySuperAgency(
                            agency.id,
                            agencyParentDrafts[agency.id] || undefined,
                          )
                          setActiveOrgAction(null)
                          if (!result.ok) toast.show(result.message ?? '親総代理店を更新できませんでした', 'error')
                          else toast.show('親総代理店を更新しました', 'success')
                        }}
                      >
                        親を保存
                      </button>
                    </div>

                    <div className="rounded-lg border border-slate-200">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-sm font-semibold text-slate-900">所属コネクター</div>
                        <Badge tone={childCount > 0 ? 'green' : 'slate'}>{childCount}人</Badge>
                      </div>

                      {childCount === 0 ? (
                        <div className="px-3 py-4 text-sm text-slate-500">所属コネクターはいません。</div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {childConnectors.map((connector) => {
                            const agencyId = connector.connector?.agencyId
                            const intro = connector.connector?.introducedById
                              ? findUser(db, connector.connector.introducedById)
                              : undefined

                            return (
                              <div key={connector.id} className="grid gap-3 px-3 py-3 xl:grid-cols-[minmax(0,1fr)_minmax(220px,0.75fr)_auto] xl:items-center">
                                <div className="min-w-0">
                                  <div className="font-semibold text-slate-900">{connector.name}</div>
                                  <div className="text-xs text-slate-500">{connector.email}</div>
                                  <div className="mt-1 text-xs text-slate-600">紹介者: {intro?.name ?? '-'}</div>
                                </div>

                                <select
                                  className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm"
                                  value={agencyId ?? ''}
                                  onChange={async (event) => {
                                    const newAgencyId = event.target.value
                                    const result = await actions.adminSetConnectorAgency(connector.id, newAgencyId)
                                    if (!result.ok) toast.show(result.message ?? '更新できませんでした', 'error')
                                    else toast.show('所属先を更新しました', 'success')
                                  }}
                                >
                                  <option value="" disabled>
                                    選択してください
                                  </option>
                                  {assignableParents.map((agencyOption) => (
                                    <option key={agencyOption.id} value={agencyOption.id}>
                                      {agencyOption.name}（{agencyOption.role}）
                                    </option>
                                  ))}
                                </select>

                                <button
                                  type="button"
                                  className={`${actionButtonClass} w-fit border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100`}
                                  onClick={() => setDeleteTarget(connector)}
                                >
                                  <Trash2 size={14} />
                                  削除
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}

          {agencyList.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              代理店はまだありません。
            </div>
          ) : null}
        </div>
      </Card>

      <Card
        title="報酬率カスタム"
        subtitle="代理店は総率、コネクターは個人率を個別設定できます。総代理店率は報酬設定画面で全体管理します。"
      >
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <div>
            全体既定: 総代理店総率 {formatPercent(db.settings.superAgencyRate)} / 代理店総率 {formatPercent(db.settings.agencyRate)} / コネクター率 {formatPercent(db.settings.connectorRate)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            代理店総率は総代理店総率以下、コネクター率は所属先の総率以下で設定してください。
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2">ユーザー</th>
                <th className="px-3 py-2">ロール / 所属</th>
                <th className="px-3 py-2">既定率</th>
                <th className="px-3 py-2">適用中</th>
                <th className="px-3 py-2">個別設定</th>
                <th className="px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {commissionUsers.map((user) => {
                const currentOverride = getCurrentOverride(user)
                const defaultRate = getDefaultRate(user, db.settings.agencyRate, db.settings.connectorRate)
                const effectiveRate = getEffectiveRate(user, db.settings.agencyRate, db.settings.connectorRate)
                const parentAgency = user.role === 'コネクター' ? findUser(db, user.connector?.agencyId) : undefined
                const maxRate =
                  parentAgency?.role === '総代理店'
                    ? db.settings.superAgencyRate
                    : parentAgency
                      ? getEffectiveRate(parentAgency, db.settings.agencyRate, db.settings.connectorRate)
                      : db.settings.agencyRate
                const isBusy = activeCommissionAction?.startsWith(`${user.id}:`) ?? false

                return (
                  <tr key={user.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-900">{user.name}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={user.role === '代理店' ? 'blue' : 'green'}>{user.role}</Badge>
                      <div className="mt-1 text-xs text-slate-500">
                        {user.role === '代理店'
                          ? `親総代理店: ${findUser(db, user.agency?.superAgencyId)?.name ?? 'なし'}`
                          : `所属先: ${parentAgency?.name ?? '-'} (${parentAgency?.role ?? '-'})`}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      <div>{formatPercent(defaultRate)}</div>
                      <div className="text-xs text-slate-500">
                        {user.role === 'コネクター' ? `上限: ${formatPercent(maxRate)}` : '全体設定'}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{formatPercent(effectiveRate)}</div>
                      <div className="text-xs text-slate-500">
                        {currentOverride === undefined ? '個別設定なし' : `個別: ${formatPercent(currentOverride)}`}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-40">
                        <div className="flex items-center gap-2">
                          <input
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                            inputMode="decimal"
                            placeholder={formatRateInput(defaultRate)}
                            value={commissionDrafts[user.id] ?? ''}
                            onChange={(event) =>
                              setCommissionDrafts((current) => ({
                                ...current,
                                [user.id]: event.target.value,
                              }))
                            }
                          />
                          <span className="text-xs text-slate-500">%</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className={`${actionButtonClass} border-slate-200 bg-white text-slate-700 hover:bg-slate-100`}
                          disabled={isBusy}
                          onClick={() => handleSaveCommission(user)}
                        >
                          保存
                        </button>
                        <button
                          type="button"
                          className={`${actionButtonClass} border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100`}
                          disabled={isBusy || currentOverride === undefined}
                          onClick={() => handleResetCommission(user)}
                        >
                          既定に戻す
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="振込口座一覧" subtitle="報酬入金先として総代理店・代理店・コネクターの振込口座を確認できます">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2">ユーザー</th>
                <th className="px-3 py-2">ロール</th>
                <th className="px-3 py-2">所属 / 招待コード</th>
                <th className="px-3 py-2">振込口座</th>
              </tr>
            </thead>
            <tbody>
              {payoutUsers.map((user) => {
                const agency = user.role === 'コネクター' ? findUser(db, user.connector?.agencyId) : undefined
                const intro = user.role === 'コネクター' ? findUser(db, user.connector?.introducedById) : undefined

                return (
                  <tr key={user.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-900">{user.name}</div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={user.role === 'コネクター' ? 'green' : 'blue'}>{user.role}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {user.role === '総代理店' ? (
                        <div>招待コード: {user.agency?.inviteCode ?? '-'}</div>
                      ) : user.role === '代理店' ? (
                        <div className="grid gap-1">
                          <div>招待コード: {user.agency?.inviteCode ?? '-'}</div>
                          <div>親総代理店: {findUser(db, user.agency?.superAgencyId)?.name ?? 'なし'}</div>
                        </div>
                      ) : (
                        <div className="grid gap-1">
                          <div>所属先: {agency?.name ?? '-'} ({agency?.role ?? '-'})</div>
                          <div>紹介者(参考): {intro?.name ?? '-'}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <BankAccountSummary bankAccount={user.bankAccount} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={!!deleteTarget}
        title="アカウントを削除"
        description="削除後はログインできなくなり、通常の一覧や招待導線から非表示になります。"
        onClose={() => {
          if (!activeDeleteAction) setDeleteTarget(null)
        }}
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={!!activeDeleteAction}
            >
              キャンセル
            </Button>
            <Button
              variant="primary"
              className="bg-rose-600 hover:brightness-110"
              leftIcon={<Trash2 size={16} />}
              onClick={handleDeletePartnerAccount}
              disabled={!deleteTarget || !!activeDeleteAction}
            >
              削除する
            </Button>
          </div>
        }
      >
        {deleteTarget ? (
          <div className="grid gap-4 text-sm text-slate-700">
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900">
              この操作は復元UIの対象外です。必要な場合は管理作業で復旧してください。
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-semibold text-slate-900">{deleteTarget.name}</div>
                <Badge tone={deleteTarget.role === 'コネクター' ? 'green' : 'blue'}>
                  {deleteTarget.role}
                </Badge>
              </div>
              <div className="mt-1 text-xs text-slate-500">{deleteTarget.email}</div>
            </div>

            <ul className="list-disc space-y-1 pl-5 text-xs text-slate-600">
              <li>商談・報酬・監査ログの履歴は保持されます。</li>
              <li>未払い報酬または申請中の出金がある場合は削除できません。</li>
              <li>代理店に配下コネクターがいる場合は先に所属先を付け替えてください。</li>
            </ul>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

function InviteCell(props: { title: string; inviteCode: string; link: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
      <div className="font-medium text-slate-700">{props.title}</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="font-mono">{props.inviteCode || '-'}</span>
        <CopyButton value={props.inviteCode} label="コードをコピー" copiedLabel="コピー済み" />
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          readOnly
          value={props.link}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
        />
        <CopyButton
          value={props.link}
          label="リンクをコピー"
          copiedLabel="コピー済み"
          className="shrink-0"
        />
      </div>
    </div>
  )
}

function BankAccountSummary(props: { bankAccount?: BankAccount }) {
  if (!props.bankAccount) {
    return <div className="text-xs text-slate-400">未登録</div>
  }

  return (
    <div className="text-xs text-slate-700">
      <div>
        {props.bankAccount.bankName} {props.bankAccount.branchName}
      </div>
      <div>
        {props.bankAccount.accountType} {props.bankAccount.accountNumber}
      </div>
      <div className="text-slate-500">{props.bankAccount.accountHolder}</div>
    </div>
  )
}

export default function AdminPartnersPage() {
  return (
    <ToastProvider>
      <AdminPartnersInner />
    </ToastProvider>
  )
}
